/* WeQuote Dev Board — staged delivery (v2 §2, ADDENDUM-PRD / verify §69–§70).
 * A big job broken into Stages released together but scheduled separately,
 * each with its own dev, estimate and optional deadline; stage n waits on
 * stage n-1 (the generalised design→dev handoff). While staged, the card's
 * overall estimate is LOCKED and derived (= sum of stage estimates); the
 * overall deadline stays editable and the projected finish is the last
 * stage's estimated completion. */
import type { Feature, Stage } from './types'
import { calcEffort } from './constants'
import type { MutResult } from './mutations'

export function normaliseStage(s: any): Stage {
  if (s.id === undefined) s.id = Math.floor(Math.random() * 1e9)
  if (s.name === undefined) s.name = ''
  if (s.dev === undefined) s.dev = ''
  if (s.time === undefined) s.time = ''
  if (s.deadline === undefined) s.deadline = ''
  if (s.start === undefined) s.start = ''
  if (s.estCompletion === undefined) s.estCompletion = ''
  if (s.schedStart === undefined) s.schedStart = ''
  if (s.progress === undefined) s.progress = 0
  if (s.done === undefined) s.done = false
  if (s.waitsOnPrev === undefined) s.waitsOnPrev = true
  return s as Stage
}
export function newStage(): Stage { return normaliseStage({}) }

/** Overall estimate for a staged card = sum of the stage estimates. */
export function stageTotalTime(f: Feature): number {
  return (f.stages || []).reduce((t, s) => { const n = parseFloat(s.time); return t + (isNaN(n) ? 0 : n) }, 0)
}
/** Keep the umbrella time + effort tier in sync with the stages. */
export function syncStageDerived(f: Feature): Feature {
  if (!f.multiStage) return f
  const total = stageTotalTime(f)
  const time = total > 0 ? String(total) : ''
  return { ...f, time, eff: calcEffort(time) || f.eff }
}
/** Index of the active (first not-done) stage, or -1 when all done / unstaged. */
export function activeStageIndex(f: Feature): number {
  if (!f.multiStage) return -1
  for (let i = 0; i < f.stages.length; i++) if (!f.stages[i].done) return i
  return -1
}

const find = (fs: Feature[], id: number) => fs.find((x) => x.id === id)
const mapId = (fs: Feature[], id: number, fn: (f: Feature) => Feature): Feature[] =>
  fs.map((f) => (f.id === id ? fn({ ...f, stages: f.stages.map((s) => ({ ...s })) }) : f))
const changed = (fs: Feature[], id: number, toast?: string): MutResult => ({ features: fs, toast, changedIds: [id] })

export function setMultiStage(fs: Feature[], id: number, on: boolean): MutResult {
  const f = find(fs, id)
  if (!f) return { features: fs }
  if (on) {
    return changed(mapId(fs, id, (g) => syncStageDerived({
      ...g,
      timeStash: g.multiStage ? g.timeStash : g.time || '', // stash the manual overall estimate
      multiStage: true,
      stages: g.stages.length ? g.stages : [newStage()],
    })), id)
  }
  return changed(mapId(fs, id, (g) => {
    const time = g.timeStash || ''
    return { ...g, multiStage: false, time, eff: calcEffort(time) || g.eff }
  }), id)
}
export function addStage(fs: Feature[], id: number): MutResult {
  return changed(mapId(fs, id, (g) => syncStageDerived({ ...g, stages: [...g.stages, newStage()] })), id)
}
export function removeStage(fs: Feature[], id: number, sid: number): MutResult {
  return changed(mapId(fs, id, (g) => syncStageDerived({ ...g, stages: g.stages.filter((s) => s.id !== sid) })), id)
}
export function setStageField(fs: Feature[], id: number, sid: number, field: keyof Stage, val: any): MutResult {
  return changed(mapId(fs, id, (g) => {
    const stages = g.stages.map((s) => (s.id === sid ? { ...s, [field]: val } : s))
    const next = { ...g, stages }
    return field === 'time' ? syncStageDerived(next) : next
  }), id)
}
/** True when this stage deadline would overrun the card's overall deadline —
 *  the view confirms, then calls setStageDeadline with pushCard accordingly. */
export function stageDeadlineNeedsPush(f: Feature, val: string): boolean {
  return !!(val && f.deadline && val > f.deadline)
}
/** Deadline guard (verify §69): pushCard=true also moves the card deadline
 *  out to match; when a push is needed and pushCard is false, NOTHING changes. */
export function setStageDeadline(fs: Feature[], id: number, sid: number, val: string, pushCard: boolean): MutResult {
  const f = find(fs, id)
  if (!f) return { features: fs }
  if (stageDeadlineNeedsPush(f, val) && !pushCard) return { features: fs }
  return changed(mapId(fs, id, (g) => ({
    ...g,
    deadline: stageDeadlineNeedsPush(g, val) ? val : g.deadline,
    stages: g.stages.map((s) => (s.id === sid ? { ...s, deadline: val } : s)),
  })), id)
}
/** Complete tick (verify §70): keeps every slot; card progress % = share of
 *  stages done. The VIEW prompts to move to Ready to Test when all are done. */
export function markStageDone(fs: Feature[], id: number, sid: number): MutResult {
  return changed(mapId(fs, id, (g) => {
    const stages = g.stages.map((s) => (s.id === sid ? { ...s, done: !s.done } : s))
    const total = stages.length
    const doneN = stages.filter((s) => s.done).length
    return { ...g, stages, progress: total ? Math.round((doneN / total) * 100) : g.progress }
  }), id)
}
export function allStagesDone(f: Feature): boolean {
  return f.multiStage && f.stages.length > 0 && f.stages.every((s) => s.done)
}
